import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingBag, Clock, XCircle, Mail, Upload, Trash2, ChevronDown, Check, X } from "lucide-react";

const PRODUCT_CATEGORY_OPTIONS = [
  "Curriculum and Learning Materials",
  "Educational Toys and Games",
  "Books Media and Printables",
  "Kids Clothing and Shoes",
  "Sports PE and Outdoor Gear",
  "Arts Crafts and Music",
  "Desks and Learning Space",
  "Tech Electronics and Lab Gear",
  "Baby and Toddler Essentials",
  "Handcrafted and Local Goods",
  "Free and Community Swap",
  "Other",
];

const PRODUCT_SLOT_COUNT = 8;
const emptySlot = () => ({ photo: "", description: "", price: "" });
const padSlots = (arr) => {
  const list = Array.isArray(arr) ? arr.slice(0, PRODUCT_SLOT_COUNT).map((s) => ({
    photo: s?.photo || "",
    description: s?.description || "",
    price: s?.price || "",
  })) : [];
  while (list.length < PRODUCT_SLOT_COUNT) list.push(emptySlot());
  return list;
};

const EMPTY = {
  business_name: "", tagline: "", description: "", categories: [],
  location: "", phone: "", contact_email: "", website: "",
  business_logo: "",
  product_categories: [],
  product_slots: padSlots([]),
  social_link_1: "", social_link_2: "", social_link_3: "",
};

export default function MarketplaceSetup() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(EMPTY);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState("");
  const logoInputRef = useRef(null);
  const slotInputRefs = useRef(Array.from({ length: PRODUCT_SLOT_COUNT }, () => ({ current: null })));

  useEffect(() => {
    api.get("/role-profile")
      .then((r) => {
        const raw = r.data.profile || {};
        setProfile({
          ...EMPTY,
          ...raw,
          product_slots: padSlots(raw.product_slots),
        });
        setStatus(r.data.vendor_status);
      })
      .catch(() => {});
  }, []);

  const upload = async (file, kind, onPath) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image is too large (max 10MB)");
    const uploadKey = typeof kind === "string" ? kind : `slot-${kind}`;
    setUploading(uploadKey);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const serverKind = typeof kind === "string" ? kind : "product_photo";
      const { data } = await api.post(`/marketplace-user/upload?kind=${serverKind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPath(data.path);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const setSlot = (idx, patch) => {
    setProfile((p) => {
      const next = p.product_slots.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...p, product_slots: next };
    });
  };

  const removeLogo = () => setProfile((p) => ({ ...p, business_logo: "" }));

  const toggleCategory = (opt) => {
    setProfile((p) => {
      const cur = p.product_categories || [];
      const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
      return { ...p, product_categories: next };
    });
  };

  const save = async () => {
    if (!profile.business_name.trim()) return toast.error("Shop or seller name is required");
    setSaving(true);
    try {
      await api.put("/role-profile", profile);
      const fresh = await refresh();
      if (fresh?.vendor_status === "approved" && fresh?.subscription_status === "active") {
        toast.success("Profile saved!");
        setSaving(false);
        return;
      }
      setSubmitted(true);
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  const finishSubmission = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    await logout();
    navigate("/login", { replace: true });
  };

  // Keep the submission dialog visible until the user manually clicks the X
  // (or closes the browser). Clicking outside or pressing Escape is already
  // suppressed on the DialogContent below. The sign-out and redirect to
  // /login happen in the X-close handler via onOpenChange, so the Protected
  // route doesn't kick the user off this page while the dialog is still open.

  const statusBanner = () => {
    if (status === "rejected") {
      return (
        <div className="bg-terracotta-light/60 border border-terracotta/30 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="mk-status-rejected">
          <XCircle className="w-5 h-5 text-terracotta-dark shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-forest">Application not approved</p>
            <p className="text-sm text-forest/70">Please reply to your registration email for more info.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="bg-amber-100/70 border border-amber-300/50 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="mk-status-pending">
        <Clock className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-forest">Awaiting admin approval</p>
          <p className="text-sm text-forest/70">Complete your profile and submit. You'll receive a payment email once approved.</p>
        </div>
      </div>
    );
  };

  const selectedCats = profile.product_categories || [];
  const slots = profile.product_slots || [];

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="marketplace-setup">
        <OnboardingProgress />
      <Dialog
        open={submitted}
        onOpenChange={(open) => {
          if (open) return;
          // Only reaches here when the user clicked the X. Sign out and
          // redirect — background logout avoided while the dialog is open
          // so the Protected route doesn't close the page prematurely.
          (async () => {
            try { await api.post("/auth/logout"); } catch { /* ignore */ }
            try { await logout(); } catch { /* ignore */ }
            navigate("/login", { replace: true });
          })();
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="mk-submitted-dialog"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-sage-light items-center justify-center">
              <Mail className="w-7 h-7 text-sage-dark" />
            </div>
            <DialogTitle className="text-center font-heading font-black text-forest text-xl">Profile submitted</DialogTitle>
            <DialogDescription className="text-center text-forest/70 pt-2" data-testid="mk-submitted-msg">
              Your profile has been submitted for approval. Please check your email for further updates and payment instructions.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>

      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center"><ShoppingBag className="w-5 h-5 text-white" /></span>
        <div>
          <h1 className="text-2xl font-black text-forest">Marketplace seller profile</h1>
          <p className="text-sm text-forest/60">Set up your shop so families can find you</p>
        </div>
      </div>

      {(status === "rejected" || user?.profile_completed) && statusBanner()}

      <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-4">
        <div>
          <Label>Business Logo</Label>
          <div className="mt-2 flex items-center gap-4">
            {profile.business_logo ? (
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                <img src={fileUrl(profile.business_logo)} alt="Business logo" className="w-full h-full object-cover" data-testid="mk-business-logo-preview" />
                <button type="button" onClick={removeLogo} className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove logo" data-testid="mk-business-logo-remove">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                <Upload className="w-6 h-6" />
              </div>
            )}
            <div>
              <input ref={logoInputRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => upload(e.target.files?.[0], "logo", (path) => setProfile((p) => ({ ...p, business_logo: path })))}
                data-testid="mk-business-logo-input" />
              <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
                onClick={() => logoInputRef.current?.click()} disabled={uploading === "logo"} data-testid="mk-business-logo-btn">
                <Upload className="w-4 h-4 mr-2" />
                {uploading === "logo" ? "Uploading..." : (profile.business_logo ? "Replace logo" : "Upload logo")}
              </Button>
              <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB.</p>
            </div>
          </div>
        </div>

        <div>
          <Label>Shop / seller name*</Label>
          <Input value={profile.business_name} onChange={(e) => setProfile({ ...profile, business_name: e.target.value })} data-testid="mk-business-name" className="mt-1" />
        </div>
        <div>
          <Label>Tagline</Label>
          <Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} data-testid="mk-tagline" className="mt-1" placeholder="e.g. Handmade wooden toys" />
        </div>
        <div>
          <Label>About your shop</Label>
          <Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} data-testid="mk-description" className="mt-1" rows={4} placeholder="What you sell, materials, delivery / collection info" />
        </div>

        <div>
          <Label>Product Categories</Label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="mt-1 w-full min-h-[42px] flex items-center justify-between gap-2 rounded-xl bg-softstone/60 border border-sage/30 px-3 py-2 text-left hover:bg-softstone/80 transition-colors"
                data-testid="mk-product-categories-trigger"
              >
                <div className="flex flex-wrap gap-1.5 flex-1">
                  {selectedCats.length === 0 ? (
                    <span className="text-sm text-forest/50">Select one or more product categories</span>
                  ) : (
                    <span className="text-sm font-semibold text-forest" data-testid="mk-product-categories-count">
                      {selectedCats.length} selected
                    </span>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-forest/50 shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[min(26rem,calc(100vw-2rem))] rounded-2xl bg-cream border-sage/20 p-2 shadow-lg" data-testid="mk-product-categories-panel">
              <div className="flex items-center justify-between px-3 py-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Pick every category that applies</p>
                <button
                  type="button"
                  onClick={() => setProfile((p) => ({ ...p, product_categories: [] }))}
                  disabled={selectedCats.length === 0}
                  className="text-[11px] font-bold text-terracotta-dark hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                  data-testid="mk-product-categories-clear"
                >
                  Clear Selection
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto">
                {PRODUCT_CATEGORY_OPTIONS.map((opt) => {
                  const on = selectedCats.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleCategory(opt)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-left transition-colors ${on ? "bg-sage-light text-sage-dark font-semibold" : "text-forest/80 hover:bg-softstone/70"}`}
                      data-testid={`mk-product-category-option-${opt}`}
                    >
                      <span>{opt}</span>
                      {on && <Check className="w-4 h-4 text-sage-dark shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          {selectedCats.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="mk-product-categories-chips">
              {selectedCats.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full pl-2.5 pr-1 py-0.5">
                  {c}
                  <button type="button" onClick={() => toggleCategory(c)} className="rounded-full hover:bg-white/60 p-0.5" aria-label={`Remove ${c}`} data-testid={`mk-product-category-chip-remove-${c}`}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Location / area</Label>
            <Input value={profile.location} onChange={(e) => setProfile({ ...profile, location: e.target.value })} data-testid="mk-location" className="mt-1" />
          </div>
          <div>
            <Label>Contact phone</Label>
            <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="mk-phone" className="mt-1" />
          </div>
          <div>
            <Label>Contact email</Label>
            <Input value={profile.contact_email} onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })} data-testid="mk-email" className="mt-1" />
          </div>
          <div>
            <Label>Website / social link</Label>
            <Input value={profile.website} onChange={(e) => setProfile({ ...profile, website: e.target.value })} data-testid="mk-website" className="mt-1" />
          </div>
        </div>

        <div className="grid gap-3">
          <div>
            <Label>Social Media Link 1</Label>
            <Input value={profile.social_link_1} onChange={(e) => setProfile({ ...profile, social_link_1: e.target.value })}
              placeholder="https://" className="mt-1" data-testid="mk-social-link-1" />
          </div>
          <div>
            <Label>Social Media Link 2</Label>
            <Input value={profile.social_link_2} onChange={(e) => setProfile({ ...profile, social_link_2: e.target.value })}
              placeholder="https://" className="mt-1" data-testid="mk-social-link-2" />
          </div>
          <div>
            <Label>Social Media Link 3</Label>
            <Input value={profile.social_link_3} onChange={(e) => setProfile({ ...profile, social_link_3: e.target.value })}
              placeholder="https://" className="mt-1" data-testid="mk-social-link-3" />
          </div>
        </div>

        <div className="pt-4 border-t border-sage/20">
          <h2 className="text-lg font-heading font-bold text-forest mb-1">Products</h2>
          <p className="text-sm text-forest/60 mb-4">Add up to 8 products. Each product has its own photo, description and price.</p>
          <div className="space-y-5" data-testid="mk-product-slots">
            {slots.map((slot, idx) => {
              const slotKey = `slot-${idx}`;
              const isUploading = uploading === slotKey;
              return (
                <div key={idx} className="rounded-2xl border border-sage/20 bg-softstone/30 p-4" data-testid={`mk-product-slot-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-forest/60">Product {idx + 1}</p>
                  </div>
                  <div className="flex items-start gap-4">
                    {slot.photo ? (
                      <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                        <img src={fileUrl(slot.photo)} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" data-testid={`mk-slot-photo-${idx}`} />
                        <button type="button" onClick={() => setSlot(idx, { photo: "" })} className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark" aria-label="Remove photo" data-testid={`mk-slot-photo-remove-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-2xl bg-white/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                        <Upload className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <input
                        ref={(el) => { slotInputRefs.current[idx] = { current: el }; }}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => upload(e.target.files?.[0], idx, (path) => setSlot(idx, { photo: path }))}
                        data-testid={`mk-slot-photo-input-${idx}`}
                      />
                      <Button type="button" variant="outline" size="sm" className="rounded-full border-sage/40 text-forest"
                        onClick={() => slotInputRefs.current[idx]?.current?.click()}
                        disabled={isUploading}
                        data-testid={`mk-slot-photo-btn-${idx}`}>
                        <Upload className="w-4 h-4 mr-2" />
                        {isUploading ? "Uploading..." : (slot.photo ? "Replace image" : "Upload image")}
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-forest/80 text-sm">Description</Label>
                    <Textarea
                      value={slot.description}
                      onChange={(e) => setSlot(idx, { description: e.target.value })}
                      rows={2}
                      placeholder="What the product is, condition, age range..."
                      className="mt-1 rounded-xl bg-white border-sage/30"
                      data-testid={`mk-slot-description-${idx}`}
                    />
                  </div>
                  <div className="mt-3">
                    <Label className="text-forest/80 text-sm">Price (Rands)</Label>
                    <div className="mt-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-forest/60 font-bold">R</span>
                      <Input
                        value={slot.price}
                        onChange={(e) => setSlot(idx, { price: e.target.value })}
                        placeholder="e.g. 150"
                        className="pl-7 rounded-xl bg-white border-sage/30"
                        data-testid={`mk-slot-price-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-sage hover:bg-sage-dark text-white w-full" data-testid="mk-save-btn">
          {saving ? "Saving..." : "Save profile"}
        </Button>
      </div>
    </div>
  );
}
